import { SvgIcon } from '@mui/material';
import {
  RiDraftLine,
  RiEyeLine,
  RiFolderCheckFill,
  RiIndeterminateCircleLine,
} from '@remixicon/react';
import CustomChip from '../../../components/CustomChip';
import { useLanguage } from '../../../contexts/language';
import { isJourneyPending } from './utils';
import {
  EntityStatus,
  JourneyReliefEventType,
  JourneyStatus,
} from '../../../types/core';
import { BADGE_CLASSNAMES } from '../../../utils/styles';

const getEntityStatusColorClasses = (status: EntityStatus) => {
  switch (status) {
    case EntityStatus.Invoiced:
      return BADGE_CLASSNAMES.GREEN;
    case EntityStatus.Submitted:
      return BADGE_CLASSNAMES.PURPLE;
    case EntityStatus.PendingApproval:
      return BADGE_CLASSNAMES.YELLOW;
    case EntityStatus.Rejected:
      return BADGE_CLASSNAMES.RED;
    case EntityStatus.NotLogged:
    case EntityStatus.Logged:
    default:
      return BADGE_CLASSNAMES.GRAY;
  }
};

const getEntityStatusIcon = (status: EntityStatus) => {
  switch (status) {
    case EntityStatus.NotLogged:
      return <SvgIcon component={RiIndeterminateCircleLine} />;
    case EntityStatus.Logged:
      return <SvgIcon component={RiDraftLine} />;
    case EntityStatus.Invoiced:
      return (
        <SvgIcon component={RiFolderCheckFill} className="fill-green-800" />
      );
    case EntityStatus.Submitted:
      return <SvgIcon component={RiEyeLine} />;
    default:
      return;
  }
};

const getJourneyStatusColorClasses = (
  status: JourneyStatus,
  isPending = false,
) => {
  switch (status) {
    case JourneyStatus.AsScheduled:
      if (isPending) {
        return BADGE_CLASSNAMES.GRAY;
      } else {
        return BADGE_CLASSNAMES.GREEN;
      }
    case JourneyStatus.Rescheduled:
      return BADGE_CLASSNAMES.YELLOW;
    case JourneyStatus.Cancelled:
      return BADGE_CLASSNAMES.RED;
    case JourneyStatus.ReliefEvent:
      return BADGE_CLASSNAMES.BLUE;
    default:
      return BADGE_CLASSNAMES.GRAY;
  }
};

export const JourneyStatusBadge = ({
  journeyStatus,
  reliefEventyType,
  journeyDay,
  journeyTime,
  small,
  showInPublicPage = false,
}: {
  journeyStatus: JourneyStatus;
  reliefEventyType?: JourneyReliefEventType | null;
  journeyDay: string;
  journeyTime: string;
  small?: boolean;
  showInPublicPage?: boolean;
}) => {
  const { t } = useLanguage();
  let label;

  const isPending = showInPublicPage
    ? false
    : isJourneyPending(journeyStatus, journeyDay, journeyTime);

  if (showInPublicPage && journeyStatus === JourneyStatus.AsScheduled) {
    label = t('content.on-time');
  } else if (isPending) {
    label = t('content.on-time');
  } else {
    label = [
      t(`journey-status.${journeyStatus}`),
      journeyStatus === JourneyStatus.ReliefEvent && reliefEventyType
        ? ` (${t(`journey-relief-event-type.${reliefEventyType}`)})`
        : '',
    ]
      .filter(Boolean)
      .join(' ');
  }

  const chipClass = getJourneyStatusColorClasses(journeyStatus, isPending);

  return <CustomChip label={label} className={chipClass} small={small} />;
};

export const JourneyEntityStatusBadge = ({
  entityStatus,
  small,
}: {
  entityStatus: EntityStatus;
  small?: boolean;
}) => {
  const chipClass = getEntityStatusColorClasses(entityStatus);
  const { t } = useLanguage();

  return (
    <CustomChip
      icon={getEntityStatusIcon(entityStatus)}
      label={t(`journey-entity-status.${entityStatus}`)}
      className={chipClass}
      small={small}
    />
  );
};

export const AdHocBadge = ({ small }: { small?: boolean }) => {
  const { t } = useLanguage();

  return (
    <CustomChip
      label={t(`content.ad-hoc`)}
      className={BADGE_CLASSNAMES.YELLOW}
      small={small}
    />
  );
};
